// Expected anatomical bone lengths for an average adult player, in metres.
// Used by the viewer's bone-length gate so a single ratio slider can sensibly
// gate every bone (a hand-to-wrist bone of 0.20 m and a knee-to-ankle of
// 0.45 m get the same "1.5x ratio" treatment, instead of needing a flat
// threshold that's lenient enough for the longest real bone).
//
// TWO keypoint layouts ship from the pipeline and BOTH must be supported:
//   - Halpe-26   (num_keypoints == 26) — has real head/neck/pelvis joints, so
//     the torso is the spine hip→neck and the head hangs off neck→head→nose;
//     this is what configs/vru_selfcalib.yaml exports today.
//   - COCO-WholeBody-133 (num_keypoints == 133) — NO neck/pelvis/head joints;
//     the torso is bridged shoulder↔hip and the face apex is the nose.
// The two layouts SHARE indices 0..16 (nose, eyes, ears, shoulders, elbows,
// wrists, hips, knees, ankles) but DIVERGE at 17+, so a single index-keyed
// table can't serve both — we pick the table by keypoint count at build time.
// Indices/edges mirror configs/halpe26.py::SKELETON and
// configs/coco_wholebody.py::SKELETON respectively; keep them synchronised.

// ---------------------------------------------------------------------------
// Halpe-26 (configs/halpe26.py)
// ---------------------------------------------------------------------------
const H = {
  nose: 0,
  left_eye: 1,  right_eye: 2,
  left_ear: 3,  right_ear: 4,
  left_shoulder: 5,  right_shoulder: 6,
  left_elbow: 7,     right_elbow: 8,
  left_wrist: 9,     right_wrist: 10,
  left_hip: 11,      right_hip: 12,
  left_knee: 13,     right_knee: 14,
  left_ankle: 15,    right_ankle: 16,
  head: 17,          neck: 18,           hip: 19,   // pelvis / root
  left_big_toe: 20,  right_big_toe: 21,
  left_small_toe: 22, right_small_toe: 23,
  left_heel: 24,     right_heel: 25,
} as const;

// (a, b) → expected length in metres. Symmetric L/R bones share a length.
// Edges mirror configs/halpe26.py::_SKELETON_NAMES exactly so every drawn bone
// has a realistic prior (no edge falls through to the tiny fallback, which is
// what previously gated the whole torso away).
const HALPE26_PRIORS_M: Array<[number, number, number]> = [
  // Spine + head chain (the torso lives HERE in Halpe — pelvis→neck→head→nose)
  [H.hip,  H.neck, 0.52],   // pelvis → neck: the full torso spine, runs long
  [H.neck, H.head, 0.20],   // neck → head centre
  [H.head, H.nose, 0.12],   // head centre → nose
  // Face (nose/eyes/ears)
  [H.nose, H.left_eye,  0.06],
  [H.nose, H.right_eye, 0.06],
  [H.left_eye,  H.left_ear,  0.12],
  [H.right_eye, H.right_ear, 0.12],
  // Shoulders hang off the neck (no shoulder↔shoulder bar in this layout)
  [H.neck, H.left_shoulder,  0.20],
  [H.neck, H.right_shoulder, 0.20],
  // Arms (upper arm ~0.30 m, forearm ~0.27 m for an adult)
  [H.left_shoulder,  H.left_elbow,  0.30],
  [H.left_elbow,     H.left_wrist,  0.27],
  [H.right_shoulder, H.right_elbow, 0.30],
  [H.right_elbow,    H.right_wrist, 0.27],
  // Pelvis → hips (the two short bones forming the pelvis fork)
  [H.hip, H.left_hip,  0.14],
  [H.hip, H.right_hip, 0.14],
  // Legs (hip-knee-ankle)
  [H.left_hip,   H.left_knee,   0.47],
  [H.left_knee,  H.left_ankle,  0.43],
  [H.right_hip,  H.right_knee,  0.47],
  [H.right_knee, H.right_ankle, 0.43],
  // Feet (ankle→heel→toes)
  [H.left_ankle,  H.left_heel,        0.08],
  [H.left_heel,   H.left_big_toe,     0.22],
  [H.left_big_toe, H.left_small_toe,  0.08],
  [H.right_ankle, H.right_heel,       0.08],
  [H.right_heel,  H.right_big_toe,    0.22],
  [H.right_big_toe, H.right_small_toe, 0.08],
];

// ---------------------------------------------------------------------------
// COCO-WholeBody-133 (configs/coco_wholebody.py)
// ---------------------------------------------------------------------------
const J = {
  nose: 0,
  left_eye: 1,  right_eye: 2,
  left_ear: 3,  right_ear: 4,
  left_shoulder: 5,  right_shoulder: 6,
  left_elbow: 7,     right_elbow: 8,
  left_wrist: 9,     right_wrist: 10,
  left_hip: 11,      right_hip: 12,
  left_knee: 13,     right_knee: 14,
  left_ankle: 15,    right_ankle: 16,
  left_big_toe: 17,  left_small_toe: 18, left_heel: 19,
  right_big_toe: 20, right_small_toe: 21, right_heel: 22,
  left_hand_base: 91,
  right_hand_base: 112,
} as const;

const COCO_WB_PRIORS_M: Array<[number, number, number]> = [
  // Legs (ankle-knee-hip) + pelvis bridge
  [J.left_ankle,  J.left_knee,  0.43],
  [J.left_knee,   J.left_hip,   0.47],
  [J.right_ankle, J.right_knee, 0.43],
  [J.right_knee,  J.right_hip,  0.47],
  [J.left_hip,    J.right_hip,  0.26],
  // Torso (shoulder↔hip — no neck/pelvis joint in WholeBody). This is the full
  // shoulder-to-hip diagonal of a standing adult, which runs long (~0.50-0.58 m);
  // 0.50 culled visibly-correct torso bridges, so size it to the upper end.
  [J.left_shoulder,  J.left_hip,      0.55],
  [J.right_shoulder, J.right_hip,     0.55],
  [J.left_shoulder,  J.right_shoulder, 0.36],
  // Arms (upper arm ~0.30 m, forearm ~0.27 m for an adult)
  [J.left_shoulder,  J.left_elbow,  0.30],
  [J.left_elbow,     J.left_wrist,  0.27],
  [J.right_shoulder, J.right_elbow, 0.30],
  [J.right_elbow,    J.right_wrist, 0.27],
  // Face (nose/eyes/ears) + ear→shoulder. The ear→shoulder edge is the ONLY
  // path joining the head to the body in WholeBody (no neck joint), and it
  // spans the whole neck diagonally — routinely 0.20-0.25 m when the head is
  // upright/forward. The old 0.18 m gated it away constantly, leaving a
  // detached floating head; size it to the realistic span so the head stays on.
  [J.left_eye,  J.right_eye, 0.07],
  [J.nose, J.left_eye,  0.06],
  [J.nose, J.right_eye, 0.06],
  [J.left_eye,  J.left_ear,  0.12],
  [J.right_eye, J.right_ear, 0.12],
  [J.left_ear,  J.left_shoulder,  0.24],
  [J.right_ear, J.right_shoulder, 0.24],
  // Feet (ankle→toes/heel)
  [J.left_ankle, J.left_big_toe,   0.20],
  [J.left_ankle, J.left_small_toe, 0.20],
  [J.left_ankle, J.left_heel,      0.08],
  [J.right_ankle, J.right_big_toe,   0.20],
  [J.right_ankle, J.right_small_toe, 0.20],
  [J.right_ankle, J.right_heel,      0.08],
  // Wrist → hand base (first segment of each hand chain). The remaining
  // per-finger bones (~0.02-0.04 m) aren't load-bearing for the body gate and
  // fall back to the default; listing the wrist segment keeps the hand from
  // being gated away when it's just close to the body.
  [J.left_wrist,  J.left_hand_base,  0.04],
  [J.right_wrist, J.right_hand_base, 0.04],
];

/** Pick the prior table that matches the loaded keypoint layout. Halpe-26 is
 *  exactly 26 joints; anything else is treated as COCO-WholeBody (133). */
function priorsFor(numKeypoints: number): Array<[number, number, number]> {
  return numKeypoints === 26 ? HALPE26_PRIORS_M : COCO_WB_PRIORS_M;
}

/**
 * Build a per-edge expected-length array in metres, in the SAME order as
 * `scene.json::skeleton_edges`. The prior table is selected from
 * `numKeypoints` (26 → Halpe-26, else COCO-WholeBody) because the two layouts
 * reuse the same indices for different joints past index 16.
 *
 * Falls back to ``fallback`` for any edge not in the chosen table (defensive:
 * keeps the viewer working if the exporter ships an unexpected edge — e.g. the
 * dense COCO finger bones, which are short, so a small fallback suits them).
 */
export function expectedBoneLengthsM(
  edges: ReadonlyArray<[number, number]>,
  numKeypoints: number,
  fallback = 0.08,
): Float32Array {
  const lookup = new Map<string, number>();
  const key = (a: number, b: number) => `${Math.min(a, b)}-${Math.max(a, b)}`;
  for (const [a, b, l] of priorsFor(numKeypoints)) lookup.set(key(a, b), l);

  const out = new Float32Array(edges.length);
  for (let i = 0; i < edges.length; i++) {
    const [a, b] = edges[i];
    out[i] = lookup.get(key(a, b)) ?? fallback;
  }
  return out;
}
